import { canonicalManufacturerIdentity, inferManufacturerFromMpn, isDescriptivePartSearchToken, normalizeMpn, parsePartSearchInput } from "./part-identity.js";
import { buildGenericCatalogueDiscoveryPlan } from "./catalogue-generic-discovery.js";
import { matchesCatalogueIndustrialEvidence } from "./catalogue-query-evidence.js";
import {
  getMarketExplorerCategory,
  isMarketExplorerSeedQuery,
  marketExplorerCategoryForProductFamily,
  type MarketExplorerCategoryId,
} from "./market-explorer.js";

export type SearchMode = "exact_part" | "product_discovery";
export type SearchIntentProvider = "deterministic" | "cloudflare" | "ollama";

export interface SearchSpecification {
  name: string;
  value: string;
  unit?: string;
}

export interface SearchPartRequest {
  partNumber: string;
  normalizedPartNumber: string;
  quantity: number;
  manufacturer?: string | null;
  projectId?: string;
  projectItemId?: string;
}

export interface SearchShorthandExpansion {
  source: string;
  expansion: string;
}

export interface SearchIntent {
  rawQuery: string;
  mode: SearchMode;
  supplierQuery: string;
  category: MarketExplorerCategoryId | null;
  manufacturer: string | null;
  productFamily: string | null;
  partNumber: string | null;
  partRequests: SearchPartRequest[];
  keywords: string[];
  specifications: SearchSpecification[];
  quantity: number | null;
  maxPriceGbp: number | null;
  conditions: string[];
  location: string | null;
  confidence: number;
  provider: SearchIntentProvider;
  explanation: string;
  shorthandExpansions?: SearchShorthandExpansion[];
}

export type SearchIntentChipKind =
  | "category"
  | "manufacturer"
  | "product_family"
  | "quantity"
  | "max_price"
  | "condition"
  | "specification"
  | "location";

export interface SearchIntentChip {
  id: string;
  kind: SearchIntentChipKind;
  label: string;
  removable: boolean;
}

export interface DiscoveryCandidate {
  partNumber?: string | null;
  manufacturer?: string | null;
  description?: string | null;
}

export interface DiscoveryMatch {
  score: number;
  reasons: string[];
}

const MANUFACTURERS: Array<{ canonical: string; aliases: string[] }> = [
  { canonical: "Siemens", aliases: ["siemens", "seimens"] },
  { canonical: "Allen-Bradley", aliases: ["allen bradley", "allen-bradley", "allen bradly", "rockwell automation", "rockwell", "a-b", "a.b.", "ab"] },
  { canonical: "Schneider Electric", aliases: ["schneider electric", "schneider", "telemecanique", "modicon"] },
  { canonical: "ABB", aliases: ["abb"] },
  { canonical: "Omron", aliases: ["omron"] },
  { canonical: "Mitsubishi Electric", aliases: ["mitsubishi electric", "mitsubishi"] },
  { canonical: "Phoenix Contact", aliases: ["phoenix contact"] },
  { canonical: "WAGO", aliases: ["wago"] },
  { canonical: "Beckhoff", aliases: ["beckhoff"] },
  { canonical: "Weidmuller", aliases: ["weidmuller", "weidmüller"] },
  { canonical: "B&R", aliases: ["b&r", "b and r", "br automation"] },
  { canonical: "Bosch Rexroth", aliases: ["bosch rexroth", "rexroth"] },
  { canonical: "Festo", aliases: ["festo"] },
  { canonical: "Pilz", aliases: ["pilz"] },
  { canonical: "SICK", aliases: ["sick"] },
  { canonical: "Keyence", aliases: ["keyence"] },
  { canonical: "Emerson", aliases: ["emerson", "ge fanuc", "ge automation"] },
  { canonical: "Yaskawa", aliases: ["yaskawa"] },
  { canonical: "Danfoss", aliases: ["danfoss"] },
  { canonical: "Eaton", aliases: ["eaton", "moeller", "crouse-hinds", "crouse hinds", "crouse-hinds series", "mtl"] },
  { canonical: "Pepperl+Fuchs", aliases: ["pepperl fuchs", "pepperl+fuchs", "p+f"] },
  { canonical: "Endress+Hauser", aliases: ["endress hauser", "endress+hauser", "e+h"] },
  { canonical: "Turck", aliases: ["turck"] },
  { canonical: "IFM", aliases: ["ifm", "ifm electronic"] },
  { canonical: "SEW-Eurodrive", aliases: ["sew eurodrive", "sew-eurodrive"] },
  { canonical: "Lenze", aliases: ["lenze"] },
  { canonical: "Delta", aliases: ["delta electronics", "delta automation"] },
];

const PRODUCT_FAMILIES: Array<{ canonical: string; pattern: RegExp }> = [
  { canonical: "ET 200SP", pattern: /\bet\s*200\s*sp\b/i },
  { canonical: "S7-1500", pattern: /\bs7[\s-]*1500\b/i },
  { canonical: "S7-1200", pattern: /\bs7[\s-]*1200\b/i },
  { canonical: "ControlLogix", pattern: /\bcontrol\s*logix\b/i },
  { canonical: "CompactLogix", pattern: /\bcompact\s*logix\b/i },
  { canonical: "Remote I/O", pattern: /\b(?:remote|distributed)\s+i\/?o\b|\bremote\s+(?:input|output)\s+module/i },
  { canonical: "Ethernet module", pattern: /\b(?:industrial\s+)?ethernet\s+(?:module|adapter|interface)|\bprofinet\s+(?:module|adapter|interface)|\bethernet[\s/]*ip\s+(?:module|adapter|interface)/i },
  { canonical: "Industrial switch", pattern: /\b(?:(?:managed|unmanaged|industrial)\s+)?ethernet\s+switch(?:es)?\b|\b(?:industrial|managed|unmanaged)\s+(?:ie\s+)?switch(?:es)?\b/i },
  { canonical: "Safety controller", pattern: /\bsafety\s+(?:plc|controller|cpu)s?\b/i },
  { canonical: "PLC processor", pattern: /\b(?:plc\s+)?(?:cpu|processor)\b|\bprogrammable\s+(?:automation|logic)\s+controller\b/i },
  { canonical: "PLC", pattern: /\bplcs?\b|programmable logic controller/i },
  { canonical: "HMI", pattern: /\bhmis?\b|human machine interface|operator panel|touch\s*screen\s+panel/i },
  { canonical: "Safety relay", pattern: /\bsafety\s+relays?\b/i },
  { canonical: "Motion controller", pattern: /\bmotion\s+controllers?\b/i },
  { canonical: "Servo drive", pattern: /\bservo\s+(?:drive|amplifier)s?\b/i },
  { canonical: "Servo motor", pattern: /\bservo\s+motors?\b/i },
  { canonical: "Variable-frequency drive", pattern: /\b(?:vfd|vsd|variable[\s-]+(?:frequency|speed)[\s-]+drive|inverter\s+drive|ac\s+drive)s?\b/i },
  { canonical: "Soft starter", pattern: /\bsoft\s+starters?\b/i },
  { canonical: "Motor starter", pattern: /\bmotor\s+starters?\b/i },
  { canonical: "Digital output module", pattern: /\bdigital\s+outputs?\b|\bdo\s+(?:modules?|cards?)\b/i },
  { canonical: "Digital input module", pattern: /\bdigital\s+inputs?\b|\bdi\s+(?:modules?|cards?)\b/i },
  { canonical: "Analogue output module", pattern: /\b(?:analogue|analog)\s+outputs?\b/i },
  { canonical: "Analogue input module", pattern: /\b(?:analogue|analog)\s+inputs?\b/i },
  { canonical: "Analogue I/O module", pattern: /\b(?:analogue|analog)\s+(?:input|output|i\/?o)\s+(?:module|card)?\b/i },
  { canonical: "Communication module", pattern: /\b(?:communication|communications|comms|network)\s+(?:module|card|adapter|interface)s?\b/i },
  { canonical: "Temperature module", pattern: /\b(?:temperature|thermocouple|rtd)\s+(?:input\s+)?(?:module|card)s?\b/i },
  { canonical: "Counter module", pattern: /\b(?:high[\s-]?speed\s+)?counter\s+(?:module|card)s?\b/i },
  { canonical: "I/O module", pattern: /\b(?:i\/?o|io)\s+(?:module|card)\b/i },
  { canonical: "Pressure transmitter", pattern: /\bpressure\s+(?:transmitter|transducer)s?\b/i },
  { canonical: "Temperature transmitter", pattern: /\btemperature\s+transmitters?\b/i },
  { canonical: "Flow instrument", pattern: /\b(?:flow\s+(?:meter|transmitter|sensor)|mass\s+flow\s+(?:meter|controller))s?\b/i },
  { canonical: "Level instrument", pattern: /\b(?:level\s+(?:meter|transmitter|sensor|switch)|radar\s+level\s+transmitter)s?\b/i },
  { canonical: "Signal conditioner", pattern: /\b(?:signal\s+(?:conditioner|isolator|converter)|galvanic\s+isolator)s?\b/i },
  { canonical: "Process controller", pattern: /\b(?:process|temperature|pid)\s+controllers?\b/i },
  { canonical: "Valve positioner", pattern: /\b(?:digital|electro[\s-]?pneumatic|smart)?\s*valve\s+positioners?\b/i },
  { canonical: "Control valve", pattern: /\b(?:process\s+)?control\s+valves?\b/i },
  { canonical: "I/P converter", pattern: /\b(?:i\s*\/\s*p|current[\s-]+to[\s-]+pressure)\s+(?:converter|transducer)s?\b/i },
  { canonical: "Intrinsic safety barrier", pattern: /\b(?:intrinsic(?:ally)?\s+safe(?:ty)?|zener)\s+barriers?\b/i },
  { canonical: "Process analyser", pattern: /\b(?:process|gas|liquid|ph|conductivity)\s+analy[sz]ers?\b/i },
  { canonical: "Recorder", pattern: /\b(?:chart|paperless|process)\s+recorders?\b|\bdata\s+loggers?\b/i },
  { canonical: "Calibration instrument", pattern: /\b(?:process|loop|pressure|temperature)\s+calibrators?\b/i },
  { canonical: "Vibration monitor", pattern: /\b(?:vibration|condition)\s+(?:monitor|transmitter|sensor)s?\b/i },
  { canonical: "Load cell", pattern: /\bload\s+cells?\b/i },
  { canonical: "Power supply", pattern: /\bpower\s+suppl(?:y|ies)\b/i },
  { canonical: "Circuit breaker", pattern: /\bcircuit\s+breakers?\b/i },
  // `contacter` is a common phonetic spelling used in buyer searches.
  { canonical: "Contactor", pattern: /\bcontact(?:or|er)s?\b/i },
  { canonical: "Sensor", pattern: /\bsensors?\b/i },
  { canonical: "Encoder", pattern: /\bencoders?\b/i },
  { canonical: "Industrial gateway", pattern: /\b(?:industrial\s+)?(?:protocol\s+)?gateways?\b/i },
  { canonical: "Industrial PC", pattern: /\b(?:industrial\s+pc|panel\s+pc|box\s+pc)s?\b/i },
  { canonical: "Industrial relay", pattern: /\b(?:(?:interface|control|industrial)\s+)?relays?\b/i },
  { canonical: "Terminal block", pattern: /\bterminal\s+blocks?\b/i },
];

const NAMED_PRODUCT_RANGES = new Set(["ET 200SP", "S7-1500", "S7-1200", "ControlLogix", "CompactLogix"]);

/** Model expansion may describe an equipment type, but cannot choose a product range. */
export const SEARCH_EXPANDABLE_PRODUCT_TYPES = PRODUCT_FAMILIES
  .map(({ canonical }) => canonical)
  .filter((family) => !NAMED_PRODUCT_RANGES.has(family));

export const PROTOCOL_PATTERNS: Array<{ canonical: string; pattern: RegExp }> = [
  { canonical: "WirelessHART", pattern: /\bwireless\s*hart\b/i },
  { canonical: "HART", pattern: /\bhart\b/i },
  { canonical: "FOUNDATION Fieldbus", pattern: /\b(?:foundation\s+fieldbus|ff\s+h1)\b/i },
  { canonical: "PROFINET", pattern: /\bprofinet\b/i },
  { canonical: "PROFIBUS", pattern: /\bprofibus(?:\s+dp|\s+pa)?\b/i },
  { canonical: "EtherNet/IP", pattern: /\bethernet\s*\/\s*ip\b|\bethernet\s*ip\b/i },
  { canonical: "EtherCAT", pattern: /\bethercat\b/i },
  { canonical: "Modbus TCP", pattern: /\bmodbus\s+tcp(?:\/ip)?\b/i },
  { canonical: "Modbus RTU", pattern: /\bmodbus\s+rtu\b/i },
  { canonical: "CANopen", pattern: /\bcanopen\b/i },
  { canonical: "DeviceNet", pattern: /\bdevicenet\b/i },
  { canonical: "IO-Link", pattern: /\bio[\s-]?link\b/i },
  { canonical: "AS-Interface", pattern: /\bas[\s-]?(?:interface|i)\b/i },
  { canonical: "CC-Link", pattern: /\bcc[\s-]?link\b/i },
  { canonical: "POWERLINK", pattern: /\bpowerlink\b/i },
  { canonical: "SERCOS", pattern: /\bsercos\b/i },
];

const STOP_WORDS = new Set([
  "a", "an", "and", "are", "at", "be", "best", "buy", "cheapest", "component", "components",
  "deal", "deals", "find", "for", "from", "get", "give", "i", "in", "industrial", "is", "looking",
  "me", "need", "of", "on", "or", "part", "parts", "please", "price", "product", "products", "show",
  "some", "something", "source", "sourcing", "that", "the", "to", "under", "want", "which", "with", "would",
]);

const CONDITION_PATTERNS: Array<{ value: string; pattern: RegExp }> = [
  { value: "factory_sealed", pattern: /\b(?:factory[\s-]+sealed|sealed)\b/i },
  { value: "refurbished", pattern: /\b(?:refurbished|refurb|reconditioned)\b/i },
  { value: "used", pattern: /\b(?:used|pre[\s-]?owned|second[\s-]?hand)\b/i },
  { value: "new", pattern: /\bnew\b/i },
];

function cleanQuery(value: string): string {
  return value.replace(/[<>;"']/g, " ").replace(/\s+/g, " ").trim().slice(0, 200);
}

/** Expand descriptive shorthand without rewriting adjoining catalogue-number segments. */
export function normalizeIndustrialSearchQuery(query: string): string {
  const ioDescription = (direction: string): string => {
    const analogue = direction.toLowerCase().startsWith("a");
    const input = direction.toLowerCase().endsWith("i");
    return `${analogue ? "analogue" : "digital"} ${input ? "input" : "output"} module`;
  };
  return query
    .replace(/(?<![a-z0-9._/+:-])(?:vsd|vfd)s?(?![a-z0-9._/+:-])|\bvariable[\s-]+speed[\s-]+drives?\b/gi, "variable frequency drive")
    .replace(/(?<![a-z0-9._/+:-])(?:psus?|pwr\s+sup)(?![a-z0-9._/+:-])/gi, "power supply")
    .replace(/\b(one|two|three|four|five|six|seven|eight|nine|ten)[\s-]+(?=channels?\b|ports?\b|poles?\b|phases?\b)/gi, (_match, count: string) => `${QUANTITY_WORDS[count.toLowerCase()]} `)
    .replace(/(?<![a-z0-9._/+:-])(\d{1,4})[\s-]*(?:channels?|ch|points?)(?![a-z0-9._/+:-])/gi, "$1 channel")
    .replace(/(?<![a-z0-9._/+:-])(\d{1,4})\s*(di|do|ai|ao)(?![a-z0-9._/+:-])/gi, (_match, count: string, direction: string) => (
      `${count} channel ${ioDescription(direction)}`
    ))
    .replace(/(?<![a-z0-9._/+:-])(di|do|ai|ao)\s+(?:cards?|modules?)\b/gi, (_match, direction: string) => ioDescription(direction))
    .replace(/\b(\d{1,4} channel)\s+(di|do|ai|ao)\b/gi, (_match, channels: string, direction: string) => (
      `${channels} ${ioDescription(direction)}`
    ))
    .replace(/\b((?:digital|analogue|analog)\s+(?:input|output))\s+cards?\b/gi, "$1 module")
    .replace(/\bmodule\s+(?:cards?|modules?)\b/gi, "module")
    .replace(/(?<![a-z0-9._/+:-])(\d+(?:\.\d+)?)\s*kw(?![a-z0-9._/+:-])/gi, "$1 kW")
    .replace(/\s+/g, " ")
    .trim();
}

function cleanModelText(value: unknown, maximumLength: number): string {
  return typeof value === "string"
    ? [...value].map((character) => character.charCodeAt(0) < 32 ? " " : character).join("")
      .replace(/[<>]/g, " ").replace(/\s+/g, " ").trim().slice(0, maximumLength)
    : "";
}

type ProductFamilyMatch = { canonical: string; index: number; end: number };

function findProductFamilyMatch(query: string): ProductFamilyMatch | null {
  for (const { canonical, pattern } of PRODUCT_FAMILIES) {
    const match = pattern.exec(query);
    if (match?.index != null) return { canonical, index: match.index, end: match.index + match[0].length };
  }
  return null;
}

export function detectProductFamily(query: string): string | null {
  return findProductFamilyMatch(query)?.canonical ?? null;
}

function findKnownManufacturer(query: string): string | null {
  const lower = query.toLocaleLowerCase("en-GB");
  return MANUFACTURERS.find(({ aliases }) => aliases.some((alias) => {
    const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`(^|[^a-z0-9._/+:-])${escaped}($|[^a-z0-9._/+:-])`, "i").test(lower);
  }))?.canonical ?? null;
}

/** Remove only aliases of the selected brand, preserving adjoining MPN tokens. */
export function stripSearchManufacturerAliases(query: string, manufacturer: string | null): string {
  const definition = MANUFACTURERS.find(({ canonical }) => canonical.toLowerCase() === manufacturer?.toLowerCase());
  if (!definition) return query;
  let result = query;
  for (const alias of definition.aliases) {
    const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    result = result.replace(new RegExp(`(^|[^a-z0-9._/+:-])${escaped}(?=$|[^a-z0-9._/+:-])`, "gi"), "$1 ");
  }
  return result.replace(/\s+/g, " ").trim();
}

const MANUFACTURER_LEADING_NOISE = new Set([
  "a", "accurate", "accuracy", "amp", "ampere", "amperes", "amperage", "amps", "an", "any", "best", "cheap", "cheapest", "coil", "compact", "current", "digital", "find",
  "high", "i", "industrial", "intrinsically", "looking", "low", "me", "need", "new", "please", "precision",
  "replacement", "require", "required", "safe", "show", "smart", "some", "source", "spare", "the", "universal",
  "used", "voltage", "want", "we", "wireless",
  "after", "can", "could", "would", "you", "your", "do", "does", "have", "has", "got", "get", "give", "help", "for", "of", "off", "or", "and", "is", "are", "going", "pls", "safety",
  "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "dozen", "pair", "pcs", "pieces", "units", "port", "ports", "pole", "poles", "channel", "channels",
  "din", "rail", "mount", "mounted", "mounting", "panel", "proximity", "photoelectric", "inductive", "capacitive", "magnetic", "optical", "pressure", "temperature",
  "no", "not", "without", "except", "excluding", "exclude", "avoid",
]);

const MANUFACTURER_ONLY_NOISE = new Set([
  ...MANUFACTURER_LEADING_NOISE,
  "automation", "channel", "channels", "component", "components", "control", "controls", "electric", "electronics", "engineering",
  "instrument", "instrumentation", "instruments", "process", "product", "products", "system", "systems",
  "technologies", "technology",
]);

function displayManufacturerCandidate(value: string): string {
  const cleaned = value.replace(/\s+/g, " ").trim();
  if (!cleaned || cleaned !== cleaned.toLowerCase()) return cleaned;
  return cleaned.split(" ").map((word) => (
    word.length <= 3 ? word.toUpperCase() : `${word[0].toUpperCase()}${word.slice(1)}`
  )).join(" ");
}

function plausibleManufacturerCandidate(value: string, options: { allowDigits?: boolean } = {}): string | null {
  let tokens = value
    .replace(new RegExp(`\\b${QUANTITY_TOKEN}\\s*(?:x|×)(?=\\s|$)`, "gi"), " ")
    .replace(/\b(?:factory[\s-]+sealed|brand\s+new|pre[\s-]?owned|second[\s-]?hand|refurbished|reconditioned)\b/gi, " ")
    .replace(/\b\d+(?:\.\d+)?\s*(?:v(?:\s*(?:dc|ac))?|ma|a|amps?|amperes?|kw|w|hp|hz|bar|psi|mm|cm)\b/gi, " ")
    .replace(/[^a-z0-9&+.'-]+/gi, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  while (tokens.length && MANUFACTURER_LEADING_NOISE.has(tokens[0].toLowerCase())) tokens = tokens.slice(1);
  while (tokens.length && MANUFACTURER_LEADING_NOISE.has(tokens[tokens.length - 1].toLowerCase())) tokens = tokens.slice(0, -1);
  if (tokens.length < 1 || tokens.length > 4) return null;
  if (tokens.every((token) => MANUFACTURER_ONLY_NOISE.has(token.toLowerCase()))) return null;
  const candidate = tokens.join(" ");
  const normalized = normalizeMpn(candidate);
  if (normalized.length < 2 || normalized.length > 50 || !/[A-Z]/.test(normalized)) return null;
  if (!options.allowDigits && /\d/.test(normalized)) return null;
  if (partNumberCandidates(candidate).length > 0) return null;
  return displayManufacturerCandidate(candidate);
}

function explicitManufacturer(query: string, familyMatch: ProductFamilyMatch | null): string | null {
  const marker = /\b(?:manufacturer|brand|made\s+by|manufactured\s+by)\s*[:=-]?\s*/i.exec(query);
  if (!marker?.index && marker?.index !== 0) return null;
  const start = marker.index + marker[0].length;
  const partStart = partNumberCandidates(query).find((candidate) => candidate.start >= start)?.start;
  const familyStart = familyMatch && familyMatch.index >= start ? familyMatch.index : undefined;
  const end = Math.min(
    ...[partStart, familyStart, query.length].filter((value): value is number => value != null),
  );
  const segment = query.slice(start, end).split(/[,;]|\b(?:with|under|below|for delivery|shipping)\b/i)[0];
  return plausibleManufacturerCandidate(segment, { allowDigits: true });
}

function findManufacturer(query: string, familyMatch: ProductFamilyMatch | null): string | null {
  const known = findKnownManufacturer(query);
  if (known) return known;
  const explicit = explicitManufacturer(query, familyMatch);
  if (explicit) return explicit;
  const partStart = partNumberCandidates(query)[0]?.start;
  const boundary = partStart ?? familyMatch?.index;
  if (boundary == null || boundary <= 0) return null;
  const leading = query.slice(0, boundary)
    .replace(/^(?:(?:can|could|would)\s+you\s+|i(?:'m|\s+am)?\s+(?:looking\s+for|after)\s+|find(?:\s+me)?\s+|show(?:\s+me)?\s+|source(?:\s+me)?\s+)/i, "")
    .replace(new RegExp(`^(?:(?:qty|quantity|need|want|require|required)\\s*[:=]?\\s*${QUANTITY_TOKEN}|${QUANTITY_TOKEN}\\s*(?:x|×))\\s+`, "i"), "");
  return plausibleManufacturerCandidate(leading, { allowDigits: partStart != null });
}

const QUANTITY_WORDS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  dozen: 12,
  pair: 2,
};

const QUANTITY_TOKEN = "(\\d{1,6}|one|two|three|four|five|six|seven|eight|nine|ten|dozen|pair)";
const QUANTITY_START = "(?:^|[^a-z0-9._/+:-])";
const QUANTITY_END = "(?![a-z0-9._/+:-])";
const QUANTITY_REQUEST = "(?:qty|quantity|need|want|require|required|source|get|find(?:\\s+me)?|looking\\s+for)";

function parseQuantityValue(value: string | undefined): number | null {
  if (!value) return null;
  const numeric = Number(value);
  const quantity = Number.isFinite(numeric) ? numeric : QUANTITY_WORDS[value.toLowerCase()];
  return Number.isInteger(quantity) && quantity > 0 ? Math.min(quantity, 100_000) : null;
}

function quantityNearPart(query: string, start: number, end: number): number | null {
  let before = query.slice(Math.max(0, start - 80), start);
  for (const { aliases } of MANUFACTURERS) {
    for (const alias of aliases) {
      const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      before = before.replace(new RegExp(`(^|[^a-z0-9])${escaped}(?=$|[^a-z0-9])`, "gi"), "$1 ");
    }
  }
  before = before.replace(/\b(?:brand\s+new|new|used|refurbished|refurb|factory[\s-]+sealed|the|a|an|replacement|spare)\b/gi, " ");
  const after = query.slice(end, Math.min(query.length, end + 28));
  const beforePatterns = [
    new RegExp(`${QUANTITY_REQUEST}\\s*[:=]?\\s*${QUANTITY_TOKEN}\\s*(?:x|×|of|off|pcs|pieces|units)?\\s*$`, "i"),
    new RegExp(`\\b${QUANTITY_TOKEN}\\s*(?:x|×)\\s*$`, "i"),
    new RegExp(`\\b${QUANTITY_TOKEN}\\s+(?:of|off|pcs|pieces|units)\\s*$`, "i"),
    new RegExp(`^\\s*${QUANTITY_TOKEN}\\s*$`, "i"),
  ];
  for (const pattern of beforePatterns) {
    const match = before.match(pattern);
    const quantity = parseQuantityValue(match?.[1]);
    if (quantity) return quantity;
  }
  const afterPatterns = [
    new RegExp(`^\\s*(?:x|×)\\s*${QUANTITY_TOKEN}${QUANTITY_END}`, "i"),
    new RegExp(`^\\s*(?:qty|quantity)\\s*[:=]?\\s*${QUANTITY_TOKEN}${QUANTITY_END}`, "i"),
  ];
  for (const pattern of afterPatterns) {
    const match = after.match(pattern);
    const quantity = parseQuantityValue(match?.[1]);
    if (quantity) return quantity;
  }
  return null;
}

function partNumberCandidates(query: string): Array<{ value: string; start: number; end: number }> {
  // Compact budget language still describes a price, not a catalogue number.
  // Keep offsets stable for nearby quantity extraction.
  query = query.replace(/\b(?:under|below|max(?:imum)?|less\s+than|up\s+to)\s*(?:£|gbp)?\s*[\d,.]+\s*(?:pounds?|gbp|quid)\b/gi, (match) => " ".repeat(match.length));
  // A compact count is unambiguous only before a recognised complete MPN.
  // Preserve unknown identities that happen to start with digits followed by x.
  query = query.replace(/(^|\s)(\d{1,6})[x×]([A-Z0-9][A-Z0-9._/+:-]{4,})/gi, (match, leading: string, count: string, part: string) => (
    inferManufacturerFromMpn(part) && parsePartSearchInput(part).partNumber
      ? `${leading}${" ".repeat(count.length + 1)}${part}`
      : match
  ));
  const candidates: Array<{ value: string; start: number; end: number }> = [];
  const quantitySuffix = new RegExp(`\\s+(?:x|×|qty|quantity)\\s*[:=]?\\s*${QUANTITY_TOKEN}\\s*$`, "i").exec(query);
  const partEnd = quantitySuffix?.index ?? query.length;
  const parsed = parsePartSearchInput(query.slice(0, partEnd));
  if (parsed.partNumber) {
    const start = Math.max(0, query.toLowerCase().indexOf(parsed.partNumber.toLowerCase()));
    return [{ value: parsed.partNumber, start, end: partEnd }];
  }

  // Siemens catalogue numbers are commonly printed with a significant space
  // after the four-character family prefix (for example 6ES5 095-8MA01).
  // Capture that complete identity before the generic token matcher below so
  // the family prefix is not mistaken for a manufacturer name and discarded.
  for (const match of query.matchAll(/\b6(?:ES|AV|GK|EP|SL|FX|BK)\d[A-Z0-9]*\s+[A-Z0-9]+(?:[._/+:-][A-Z0-9]+)+(?=$|[^A-Z0-9._/+:-])/gi)) {
    const value = match[0].replace(/[.,;:]+$/, "");
    const start = match.index ?? 0;
    candidates.push({ value, start, end: start + value.length });
  }

  for (const match of query.matchAll(/(^|[^A-Z0-9])([A-Z0-9][A-Z0-9._/+:-]{4,})(?=$|[^A-Z0-9._/+:-])/gi)) {
    const value = match[2].replace(/[.,;:]+$/, "");
    const normalized = normalizeMpn(value);
    const hasLetters = /[A-Z]/.test(normalized);
    const hasDigits = /\d/.test(normalized);
    const looksLikeMeasurement = /^\d+(?:V(?:DC|AC)?|MA|A|KW|W|HP|HZ|CHANNELS?|CH|POINTS?|PORTS?|POLES?|BITS?|GB|MB|GBP|EUR)$/i.test(normalized);
    const valid = !looksLikeMeasurement && !isDescriptivePartSearchToken(value) && (
      /^\d{7,}$/.test(normalized)
      || (normalized.length >= 7 && hasLetters && hasDigits)
    );
    if (!valid) continue;
    const start = (match.index ?? 0) + match[1].length;
    const end = start + value.length;
    if (candidates.some((candidate) => start >= candidate.start && end <= candidate.end)) continue;
    candidates.push({ value, start, end });
  }

  return candidates.sort((left, right) => left.start - right.start || right.end - right.start - (left.end - left.start));
}

export function extractPartRequests(query: string, limit = 5): SearchPartRequest[] {
  const unique = new Map<string, SearchPartRequest>();
  // Split before whitespace normalization: a Siemens number can contain real
  // spaces, but must never absorb the next pasted line as part of its identity.
  const entries = query.slice(0, 200).split(/[\r\n;,]+|\s+(?:and|plus|&)\s+/i).map(segment => {
    const cleaned = cleanQuery(segment);
    return { cleaned, candidates: partNumberCandidates(cleaned) };
  });
  // A sole part followed by an explicit quantity-only fragment is unambiguous.
  // Additional part entries or nouns such as "qty 5 relays" keep their own count.
  const trailingQuantity = entries.length === 2 && entries[0].candidates.length === 1
    && new RegExp(`^(?:qty|quantity)\\s*[:=]?\\s*${QUANTITY_TOKEN}\\s*$`, "i").test(entries[1].cleaned)
    ? extractQuantity(entries[1].cleaned) : null;
  for (const { cleaned, candidates } of entries) {
    // A quantity separated from the MPN by descriptive words still belongs to
    // this entry. Do not borrow it from another part or a separate list entry.
    const entryQuantity = candidates.length === 1 ? quantityAtEndOfEntry(cleaned) : null;
    for (const candidate of candidates) {
      const normalizedPartNumber = normalizeMpn(candidate.value);
      if (unique.has(normalizedPartNumber)) continue;
      unique.set(normalizedPartNumber, {
        partNumber: candidate.value,
        normalizedPartNumber,
        quantity: quantityNearPart(cleaned, candidate.start, candidate.end) ?? entryQuantity ?? trailingQuantity ?? 1,
      });
      if (unique.size >= Math.max(1, Math.min(10, limit))) return [...unique.values()];
    }
  }
  return [...unique.values()];
}

export function extractLikelyPartNumber(query: string): string | null {
  return extractPartRequests(query, 1)[0]?.partNumber ?? null;
}

export function looksLikeExactPartNumber(query: string): boolean {
  return extractLikelyPartNumber(query) !== null;
}

function extractQuantity(query: string): number | null {
  const match = query.match(new RegExp(`\\b${QUANTITY_REQUEST}\\s*[:=]?\\s*(?:a\\s+)?${QUANTITY_TOKEN}${QUANTITY_END}(?!\\s*(?:channels?|ch|ports?|poles?|phases?|volts?|v(?:dc|ac)?|amps?|a|kw|hp)\\b)`, "i"))
    ?? query.match(new RegExp(`${QUANTITY_START}${QUANTITY_TOKEN}\\s*(?:x|×|of|off|pcs|pieces|units)\\b`, "i"));
  return parseQuantityValue(match?.[1]);
}

function quantityAtEndOfEntry(query: string): number | null {
  const match = query.match(new RegExp(`${QUANTITY_START}(?:qty|quantity)\\s*[:=]?\\s*${QUANTITY_TOKEN}${QUANTITY_END}(?:\\s+(?:pcs|pieces|units))?\\s*$`, "i"))
    ?? query.match(new RegExp(`${QUANTITY_START}${QUANTITY_TOKEN}\\s+(?:pcs|pieces|units)\\s*$`, "i"));
  return parseQuantityValue(match?.[1]);
}

function extractMaxPrice(query: string): number | null {
  const match = query.match(/(?:\b(?:under|below|max(?:imum)?|less\s+than|up\s+to)\s*)?(?:£|gbp\s*)\s*([\d,]+(?:\.\d{1,2})?)/i)
    ?? query.match(/\b(?:under|below|max(?:imum)?|less\s+than|up\s+to)\s*([\d,]+(?:\.\d{1,2})?)\s*(?:pounds?|gbp|quid)\b/i);
  if (!match) return null;
  const value = Number(match[1].replace(/,/g, ""));
  return Number.isFinite(value) && value > 0 ? Math.min(value, 10_000_000) : null;
}

function extractConditions(query: string): string[] {
  const all = CONDITION_PATTERNS.map(({ value }) => value);
  const unrestricted = /\b(?:any\s+condition|all\s+conditions|condition\s+(?:doesn[\s’']?t|does\s+not)\s+matter|(?:don[\s’']?t|do\s+not)\s+mind\s+(?:the\s+)?condition)\b/i.test(query);
  // “Don't mind used” expresses acceptance. Remove that phrase before looking
  // for exclusions so the ordinary word “not” cannot reverse its meaning.
  const cleaned = query.replace(/\b(?:don[\s’']?t|do\s+not)\s+mind\s+/gi, " ");
  const conditionWords = "(?:factory[\\s-]+sealed|sealed|refurbished|refurb|reconditioned|used|pre[\\s-]?owned|second[\\s-]?hand|new)";
  const negativePrefix = new RegExp(`\\b(?:no|not|without|except|excluding|exclude|avoid)\\s+(?:any\\s+)?(?:${conditionWords}\\s*(?:or|and|/|,)\\s*)*$`, "i");
  const positive = new Set<string>();
  const negative = new Set<string>();
  for (const { value, pattern } of CONDITION_PATTERNS) {
    for (const match of cleaned.matchAll(new RegExp(pattern.source, "gi"))) {
      const explicitlyAccepted = /^\s+(?:is\s+)?(?:fine|ok(?:ay)?|acceptable|allowed)\b/i.test(cleaned.slice((match.index ?? 0) + match[0].length));
      (negativePrefix.test(cleaned.slice(0, match.index)) && !explicitlyAccepted ? negative : positive).add(value);
    }
  }
  // “Not new” also excludes the more specific factory-sealed new condition.
  if (negative.has("new")) negative.add("factory_sealed");
  if (!unrestricted && positive.size === 0 && negative.size === 0) return [];
  const allowed = unrestricted || positive.size === 0 ? all : [...positive];
  return all.filter((value) => allowed.includes(value) && !negative.has(value));
}

function extractLocation(query: string): string | null {
  if (/\b(?:uk|u\.k\.|united kingdom|great britain|britain)\b/i.test(query)) return "United Kingdom";
  if (/\bireland\b/i.test(query)) return "Ireland";
  if (/\b(?:europe|european union|eu)\b/i.test(query)) return "Europe";
  if (/\b(?:germany|deutschland)\b/i.test(query)) return "Germany";
  if (/\bfrance\b/i.test(query)) return "France";
  if (/\b(?:netherlands|holland)\b/i.test(query)) return "Netherlands";
  if (/\bbelgium\b/i.test(query)) return "Belgium";
  return null;
}

export function extractSearchSpecifications(query: string): SearchSpecification[] {
  query = normalizeIndustrialSearchQuery(query);
  const specifications: SearchSpecification[] = [];
  const seen = new Set<string>();
  const measurementRanges: Array<{ start: number; end: number }> = [];
  const measurementUnit = "(?:v(?:olts?)?\\s*(?:dc|ac)?|milliamps?|milliamperes?|ma|amps?|amperes?|a|kw|w|hp|hz)";
  const measurementAtEnd = new RegExp(`\\b\\d+(?:\\.\\d+)?\\s*${measurementUnit}\\s*$`, "i");
  const measurementAtStart = new RegExp(`^\\s*\\d+(?:\\.\\d+)?\\s*${measurementUnit}\\b`, "i");
  const canonicalUnit = (value: string): string => {
    const compact = value.toLocaleLowerCase("en-GB").replace(/\s+/g, "");
    if (/^(?:ma|milliamps?|milliamperes?)$/.test(compact)) return "MA";
    if (/^(?:a|amps?|amperes?)$/.test(compact)) return "A";
    if (/^(?:v|volt|volts)(?:dc|ac)?$/.test(compact)) {
      if (compact.endsWith("dc")) return "VDC";
      if (compact.endsWith("ac")) return "VAC";
      return "V";
    }
    return compact.toUpperCase();
  };
  const measurementName = (
    unit: string,
    index: number,
    length: number,
    range: boolean,
  ): string => {
    const before = query.slice(Math.max(0, index - 40), index);
    const after = query.slice(index + length, Math.min(query.length, index + length + 40));
    const hasForwardLabel = (pattern: string): boolean => {
      const match = before.match(new RegExp(`(${pattern})\\s*$`, "i"));
      if (!match || match.index == null) return false;
      // In `230 V coil, 24 V auxiliary`, the word `coil` labels the value to
      // its left. Do not let that trailing label leak onto the next value.
      return !measurementAtEnd.test(before.slice(0, match.index));
    };
    const hasBackwardLabel = (pattern: string, prefix = "\\s*[:=,;/-]?\\s*"): boolean => {
      const match = after.match(new RegExp(`^${prefix}(${pattern})\\b`, "i"));
      if (!match) return false;
      // In `32 A, rated current 40 A`, the label introduces the next value;
      // it does not describe the 32 A value to its left.
      return !measurementAtStart.test(after.slice(match[0].length));
    };
    const coilTerm = "(?:coil(?:\\s+voltage)?|control(?:\\s+(?:circuit|supply))?\\s+voltage)";
    const labelLink = "(?:\\s+(?:is|of|at|rated(?:\\s+at)?|rating(?:\\s+(?:is|of|at))?))*";
    const explicitCoilContext = hasForwardLabel(`${coilTerm}${labelLink}\\s*[:=,;/-]?`)
      || hasBackwardLabel(coilTerm, "\\s*[:=,;/-]?\\s*(?:for\\s+(?:the\\s+)?)?");
    if (unit.startsWith("V") && explicitCoilContext) return range ? "coil voltage range" : "coil voltage";
    const ratedCurrentTerm = "(?:rated(?:\\s+operational)?\\s+current|operational\\s+current|current\\s+rating|main(?:\\s+circuit)?\\s+current|i[eth])";
    const contactorTerm = "contact(?:or|er)s?";
    const contactorModifiers = "(?:(?:rated|safety|industrial|power|motor|reversing|non[\\s-]?reversing|miniature|ac[\\s-]?\\d+)\\s+){0,3}";
    const auxiliaryCurrentTerm = "(?:auxiliary(?:\\s+contacts?)?(?:\\s+(?:current(?:\\s+rating)?|rating))?|coil\\s+current)";
    const auxiliaryCurrentContext = hasForwardLabel(`${auxiliaryCurrentTerm}${labelLink}\\s*[:=,;/-]?`)
      || hasBackwardLabel(auxiliaryCurrentTerm);
    const explicitRatedCurrentContext = hasForwardLabel(`${ratedCurrentTerm}${labelLink}\\s*[:=,;/-]?`)
      || hasBackwardLabel(ratedCurrentTerm)
      || new RegExp(`\\b${contactorTerm}(?:\\s+(?:is\\s+)?rated(?:\\s+at)?)?\\s*[,;:/-]?\\s*$`, "i").test(before)
      || new RegExp(`^\\s*[,;:/-]?\\s*${contactorModifiers}${contactorTerm}\\b`, "i").test(after);
    if ((unit === "A" || unit === "MA") && explicitRatedCurrentContext && !auxiliaryCurrentContext) {
      return range ? "rated current range" : "rated current";
    }
    if (unit === "A" || unit === "MA") return range ? "current range" : "current";
    if (unit.startsWith("V")) return range ? "voltage range" : "voltage";
    if (unit === "HZ") return "frequency";
    return "power";
  };
  for (const match of query.matchAll(new RegExp(`\\b(\\d+(?:\\.\\d+)?)\\s*[-–]\\s*(\\d+(?:\\.\\d+)?)\\s*(${measurementUnit})\\b`, "gi"))) {
    measurementRanges.push({ start: match.index ?? 0, end: (match.index ?? 0) + match[0].length });
    const unit = canonicalUnit(match[3]);
    const value = `${match[1]}-${match[2]}`;
    const name = measurementName(unit, match.index ?? 0, match[0].length, true);
    const key = `${name}:${value}:${unit}`;
    if (seen.has(key)) continue;
    seen.add(key);
    specifications.push({ name, value, unit });
  }
  for (const match of query.matchAll(new RegExp(`\\b(\\d+(?:\\.\\d+)?)\\s*(${measurementUnit})\\b`, "gi"))) {
    if (measurementRanges.some(({ start, end }) => (match.index ?? 0) >= start && (match.index ?? 0) < end)) continue;
    const unit = canonicalUnit(match[2]);
    const name = measurementName(unit, match.index ?? 0, match[0].length, false);
    const key = `${name}:${match[1]}:${unit}`;
    if (seen.has(key)) continue;
    seen.add(key);
    specifications.push({ name, value: match[1], unit });
  }
  const points = query.match(/\b(\d{1,4})[\s-]*(?:points?|channels?|ch)\b/i);
  if (points) specifications.push({ name: "channels", value: points[1] });
  const ports = query.match(/\b(\d{1,3})\s*(?:port|ports)\b/i);
  if (ports) specifications.push({ name: "ports", value: ports[1] });
  const poles = query.match(/\b(\d{1,2})\s*(?:pole|poles)\b/i);
  if (poles) specifications.push({ name: "poles", value: poles[1] });
  const ipRating = query.match(/\bIP\s*([0-6][0-9K])\b/i);
  if (ipRating) specifications.push({ name: "ingress protection", value: `IP${ipRating[1].toUpperCase()}` });
  const memory = query.match(/\b(\d+(?:\.\d+)?)\s*(KB|MB|GB)\s+(?:memory|ram|storage)\b/i);
  if (memory) specifications.push({ name: "memory", value: memory[1], unit: memory[2].toUpperCase() });
  const phases = query.match(/\b([13])\s*[\s-]?(?:phase|ph)\b/i);
  if (phases) specifications.push({ name: "phases", value: phases[1] });
  for (const protocol of PROTOCOL_PATTERNS) {
    if (protocol.pattern.test(query)) specifications.push({ name: "protocol", value: protocol.canonical });
  }
  return specifications.slice(0, 12);
}

function meaningfulKeywords(
  query: string,
  manufacturer: string | null,
  productFamily: string | null,
  specifications: readonly SearchSpecification[],
): string[] {
  const manufacturerWords = new Set((manufacturer ?? "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean));
  const familyPattern = PRODUCT_FAMILIES.find(({ canonical }) => canonical === productFamily)?.pattern;
  const hasCoilSpecification = specifications.some(({ name }) => name.startsWith("coil "));
  const seen = new Set<string>();
  const keywords: string[] = [];
  for (const token of query.toLowerCase().match(/[a-z0-9][a-z0-9+./-]*/g) ?? []) {
    const normalized = token.replace(/[^a-z0-9]/g, "");
    if (normalized.length < 2 || /^\d+(?:\.\d+)?$/.test(normalized) || STOP_WORDS.has(normalized) || manufacturerWords.has(normalized)) continue;
    if (familyPattern?.test(token)) continue;
    if (/^\d+(?:\.\d+)?(?:v(?:dc|ac)?|ma|a|amps?|amperes?|kw|w|hp|hz|channels?|ch|points?)$/.test(normalized)) continue;
    if (/^(?:channels?|ch|points?|kw)$/.test(normalized)) continue;
    if (["ac", "dc", "amp", "amps", "ampere", "amperes"].includes(normalized)) continue;
    if (hasCoilSpecification && /^coils?$/.test(normalized)) continue;
    if (CONDITION_PATTERNS.some(({ value }) => value.replace(/_/g, "") === normalized)) continue;
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    keywords.push(token);
  }
  return keywords.slice(0, 10);
}

function buildSupplierQuery(query: string, manufacturer: string | null, family: string | null, keywords: string[]): string {
  let cleaned = normalizeIndustrialSearchQuery(query)
    .replace(new RegExp(`\\b${QUANTITY_REQUEST}\\s*[:=]?\\s*(?:a\\s+)?${QUANTITY_TOKEN}${QUANTITY_END}(?!\\s*(?:channels?|ch|ports?|poles?|phases?|volts?|v(?:dc|ac)?|amps?|a|kw|hp)\\b)(?:\\s+(?:of|off|pcs|pieces|units))?`, "gi"), " ")
    .replace(new RegExp(`\\b(?:a\\s+)?${QUANTITY_TOKEN}\\s+(?:of|off|pcs|pieces|units)\\b`, "gi"), " ")
    .replace(/\b(?:under|below|max(?:imum)?|less\s+than|up\s+to)\s*(?:£|gbp)?\s*[\d,.]+(?:\s*(?:pounds?|gbp|quid))?/gi, " ")
    .replace(/(?:£|gbp\s*)\s*[\d,.]+/gi, " ")
    .replace(/\b(?:(?:can|could|would)\s+you|do\s+you\s+have|have\s+you\s+got|got\s+any|(?:i|we)(?:[\s’']?m|\s+am|\s+are)?\s+(?:need|want|looking\s+for|after)|looking\s+for|help\s+me|find|show|get|give|source|need|want)\s+(?:me\s+)?/gi, " ")
    .replace(/^\s*(?:i|we)\s+/i, " ")
    .replace(/\b(?:please|pls|cheapest|best|deal|deals|price|pricing|available|availability)\b/gi, " ")
    .replace(/\b(?:delivered?|shipping|ship)\s+(?:to|into)\s+(?:the\s+)?(?:uk|u\.k\.|united kingdom|great britain|britain|ireland|europe|eu|germany|france|netherlands|belgium)\b/gi, " ")
    .replace(/\b(?:in|within)\s+(?:the\s+)?(?:uk|u\.k\.|united kingdom|great britain|britain|ireland|europe|eu)\b/gi, " ")
    .replace(/\bcontacter(s?)\b/gi, "contactor$1")
    .replace(/\b(\d+(?:\.\d+)?)\s*(?:amps?|amperes?)\b/gi, "$1A")
    .replace(/\b(?:amps?|amperes?)\b/gi, " ")
    .replace(/\b(\d+(?:\.\d+)?)\s*v\s*(dc|ac)\b/gi, (_match, value: string, currentType: string) => (
      `${value}V${currentType.toUpperCase()}`
    ))
    .replace(/[?!]+/g, " ")
    .replace(/^\s*(?:a|an|any)\s+/i, " ")
    .replace(/\s+/g, " ")
    .trim();
  const brand = MANUFACTURERS.find(({ canonical }) => canonical === manufacturer);
  if (brand) {
    const aliases = [...brand.aliases].sort((left, right) => right.length - left.length)
      .map((alias) => alias.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
    cleaned = cleaned.replace(new RegExp(`(^|[^a-z0-9._/+:-])(${aliases})(?=$|[^a-z0-9._/+:-])`, "gi"), (_match, leading: string, alias: string) => (
      `${leading}${normalizeMpn(alias) === normalizeMpn(manufacturer) ? alias : manufacturer}`
    ));
  }
  if (cleaned.length < 2) cleaned = [manufacturer, family, ...keywords].filter(Boolean).join(" ");
  return cleaned.slice(0, 120);
}

export function buildDeterministicSearchIntent(
  input: string,
  requestedCategory: MarketExplorerCategoryId | null = null,
): SearchIntent {
  const rawQuery = cleanQuery(input);
  const partRequests = extractPartRequests(input);
  const partNumber = partRequests[0]?.partNumber ?? null;
  const mode: SearchMode = partNumber ? "exact_part" : "product_discovery";
  const descriptiveQuery = partNumber ? rawQuery : normalizeIndustrialSearchQuery(rawQuery);
  const productFamilyMatch = findProductFamilyMatch(descriptiveQuery);
  const categorySeed = mode === "product_discovery" && isMarketExplorerSeedQuery(rawQuery, requestedCategory);
  const manufacturer = findManufacturer(descriptiveQuery, productFamilyMatch)
    ?? inferManufacturerFromMpn(partNumber ?? (isDescriptivePartSearchToken(rawQuery) ? rawQuery : null));
  const productFamily = categorySeed ? null : productFamilyMatch?.canonical ?? null;
  const category = requestedCategory ?? marketExplorerCategoryForProductFamily(productFamily);
  const specifications = extractSearchSpecifications(rawQuery);
  const keywords = categorySeed ? [] : meaningfulKeywords(descriptiveQuery, manufacturer, productFamily, specifications);
  const quantity = partRequests[0]?.quantity ?? extractQuantity(rawQuery);
  const maxPriceGbp = extractMaxPrice(rawQuery);
  const conditions = extractConditions(rawQuery);
  const location = extractLocation(rawQuery);
  const categoryDefinition = getMarketExplorerCategory(category);
  const supplierQuery = partNumber
    ?? (categorySeed && categoryDefinition
      ? categoryDefinition.supplierQuery
      : buildSupplierQuery(rawQuery, manufacturer, productFamily, keywords));
  const explanation = mode === "exact_part"
    ? `Searching suppliers for the exact part number ${partNumber}.`
    : `Searching for products that match ${[getMarketExplorerCategory(category)?.label, manufacturer, productFamily, ...keywords.slice(0, 3)].filter(Boolean).join(", ") || "the requested description"}.`;

  return {
    rawQuery,
    mode,
    supplierQuery,
    category,
    manufacturer,
    productFamily,
    partNumber,
    partRequests,
    keywords,
    specifications,
    quantity,
    maxPriceGbp,
    conditions,
    location,
    confidence: mode === "exact_part" ? 0.99 : 0.7,
    provider: "deterministic",
    explanation,
  };
}

/**
 * Builds the exact intent used after a user selects a persisted catalogue
 * product. Catalogue identity has already supplied the manufacturer and MPN,
 * so it must not be reclassified by free-text heuristics or an AI interpreter.
 */
export function buildCataloguePartSearchIntent(
  input: string,
  manufacturer: string | null = null,
): SearchIntent {
  const fallback = buildDeterministicSearchIntent(input, null);
  const partNumber = fallback.rawQuery;
  const normalizedPartNumber = normalizeMpn(partNumber);
  return {
    ...fallback,
    mode: "exact_part",
    supplierQuery: partNumber,
    category: null,
    manufacturer: manufacturer?.trim() || fallback.manufacturer,
    productFamily: null,
    partNumber,
    partRequests: [{
      partNumber,
      normalizedPartNumber,
      quantity: fallback.quantity ?? 1,
    }],
    keywords: [],
    specifications: [],
    confidence: 1,
    provider: "deterministic",
    explanation: `Searching suppliers for the exact catalogue part number ${partNumber}.`,
  };
}

function finiteNumber(value: unknown, minimum: number, maximum: number): number | null {
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) && number >= minimum && number <= maximum ? number : null;
}

function shorthandCorrespondsToExpansion(source: string, expansion: string): boolean {
  const sourceWords = source.toLowerCase().split(/[\s-]+/);
  const expandedWords: string[] = expansion.toLowerCase().match(/[a-z]+/g) ?? [];
  const variants: string[][] = [expandedWords];
  if (/^(?:module|card|unit)$/.test(expandedWords[expandedWords.length - 1] ?? "")) variants.push(expandedWords.slice(0, -1));
  return variants.some((words) => {
    if (sourceWords.length === 1 && words.length >= 2) {
      return sourceWords[0] === words.map((word) => word[0]).join("");
    }
    // Compressed words such as `pwr sup` must retain their letters in order.
    return sourceWords.length >= 2 && sourceWords.length === words.length && sourceWords.every((short, index) => {
      if (short.length < 2 || short.length > words[index].length || short[0] !== words[index][0]) return false;
      let offset = 0;
      for (const letter of short) {
        offset = words[index].indexOf(letter, offset);
        if (offset < 0) return false;
        offset += 1;
      }
      return true;
    });
  });
}

function expandModelShorthand(fallback: SearchIntent, proposals: unknown): SearchIntent | null {
  if (!Array.isArray(proposals) || proposals.length === 0 || proposals.length > 4) return null;
  const accepted: Array<SearchShorthandExpansion & { start: number; end: number; family: string; confidence: number }> = [];
  for (const value of proposals) {
    if (!value || typeof value !== "object") return null;
    const record = value as Record<string, unknown>;
    const source = cleanModelText(record.source, 40);
    const expansion = cleanModelText(record.expansion, 80);
    const confidence = typeof record.confidence === "number" ? record.confidence : NaN;
    if (record.ambiguous !== false || !Number.isFinite(confidence) || confidence < 0.9 || confidence > 1) return null;
    if (!/^[a-z]{2,8}(?:[\s-][a-z]{2,8}){0,2}$/i.test(source)
      || !/^[a-z][a-z\s/-]{3,79}$/i.test(expansion)
      || STOP_WORDS.has(source.toLowerCase()) || findKnownManufacturer(source)) return null;
    const family = detectProductFamily(expansion);
    if (!family || !SEARCH_EXPANDABLE_PRODUCT_TYPES.includes(family)
      || (fallback.productFamily && family !== fallback.productFamily)
      || !shorthandCorrespondsToExpansion(source, expansion)) return null;
    // A bare two-letter acronym such as AI needs industrial context before it
    // can become a restrictive product-type filter.
    if (source.length <= 2 && !fallback.manufacturer && fallback.specifications.length === 0
      && !/\b(?:cards?|modules?|plc|industrial)\b/i.test(fallback.rawQuery)) return null;
    const escapedSource = source.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+");
    const pattern = new RegExp(`(?<![a-z0-9._/+:-])${escapedSource}(?![a-z0-9._/+:-])`, "gi");
    const matches = [...fallback.rawQuery.matchAll(pattern)];
    if (matches.length !== 1) return null;
    const start = matches[0].index!;
    const end = start + matches[0][0].length;
    if (accepted.some((other) => start < other.end && end > other.start)) return null;
    accepted.push({ source: matches[0][0], expansion, start, end, family, confidence });
  }
  if (new Set(accepted.map(({ family }) => family)).size !== 1) return null;
  let expandedQuery = fallback.rawQuery;
  for (const { start, end, expansion } of [...accepted].sort((a, b) => b.start - a.start)) {
    // A trailing generic `unit` explains acronyms such as PSU but should not
    // become an extra mandatory catalogue term.
    const queryExpansion = expansion.replace(/\s+unit$/i, "");
    expandedQuery = `${expandedQuery.slice(0, start)}${queryExpansion}${expandedQuery.slice(end)}`;
  }
  if (expandedQuery.length > 200) return null;
  expandedQuery = normalizeIndustrialSearchQuery(expandedQuery)
    .replace(/\b(module|card)\s+(?:module|card)\b/gi, "module");
  const productFamily = accepted[0].family;
  const keywords = meaningfulKeywords(expandedQuery, fallback.manufacturer, productFamily, fallback.specifications);
  const supplierQuery = buildSupplierQuery(expandedQuery, fallback.manufacturer, productFamily, keywords);
  const supplierSpecifications = extractSearchSpecifications(supplierQuery);
  if (fallback.specifications.some((requested) => !supplierSpecifications.some((candidate) => (
    requested.name === candidate.name && searchSpecificationsCompatible(requested, candidate)
  )))) return null;
  return {
    ...fallback,
    supplierQuery,
    productFamily,
    category: fallback.category ?? marketExplorerCategoryForProductFamily(productFamily),
    keywords,
    shorthandExpansions: accepted.map(({ source, expansion }) => ({ source, expansion })),
    confidence: Math.min(...accepted.map(({ confidence }) => confidence)),
    explanation: `Interpreted ${accepted.map(({ source, expansion }) => `${source} as ${expansion}`).join(", ")}.`,
  };
}

/**
 * Treats model output as untrusted. Search constraints and exact identity stay
 * deterministic; only validated shorthand may add words to the supplier query.
 */
export function validateSearchIntent(
  input: string,
  value: unknown,
  provider: Exclude<SearchIntentProvider, "deterministic"> = "cloudflare",
  requestedCategory: MarketExplorerCategoryId | null = null,
): SearchIntent {
  const fallback = buildDeterministicSearchIntent(input, requestedCategory);
  if (fallback.mode === "exact_part" || !value || typeof value !== "object") return fallback;
  const record = value as Record<string, unknown>;
  const expanded = expandModelShorthand(fallback, record.shorthandExpansions);
  if (expanded) return { ...expanded, provider };
  // Rejected expansion proposals must not re-enter through other model fields.
  if (Array.isArray(record.shorthandExpansions) && record.shorthandExpansions.length > 0) return fallback;
  const normalizedRawWords = new Set(normalizedSearchText(fallback.rawQuery).split(" ").filter(Boolean));
  const isGroundedText = (text: string) => normalizedSearchText(text)
    .split(" ")
    .filter((token) => token.length >= 2)
    .every((token) => normalizedRawWords.has(token));
  const proposedKeywords = Array.isArray(record.keywords) ? record.keywords : [];
  const keywords = [...new Set(proposedKeywords
    .map((entry) => cleanModelText(entry, 40).toLowerCase())
    .filter((entry) => entry.length >= 2 && isGroundedText(entry)))].slice(0, 10);
  // Retrieval uses supplierQuery, so preserving ratings only in the displayed
  // specifications is insufficient. Keep the complete deterministic query
  // unless an explicit, validated expansion was applied above.
  const proposedProductFamily = cleanModelText(record.productFamily, 60);
  const productFamily = fallback.productFamily || (proposedProductFamily && isGroundedText(proposedProductFamily)
    ? proposedProductFamily
    : null);
  const proposedManufacturer = cleanModelText(record.manufacturer, 80);
  const manufacturer = fallback.manufacturer || (
    proposedManufacturer && isGroundedText(proposedManufacturer)
      ? plausibleManufacturerCandidate(proposedManufacturer, { allowDigits: true })
      : null
  );

  return {
    ...fallback,
    manufacturer,
    productFamily,
    keywords: keywords.length ? keywords : fallback.keywords,
    confidence: finiteNumber(record.confidence, 0, 1) ?? 0.8,
    provider,
    explanation: `AI-assisted interpretation: ${[manufacturer, productFamily, ...(keywords.length ? keywords : fallback.keywords).slice(0, 3)].filter(Boolean).join(", ") || "industrial automation product search"}.`,
  };
}

function normalizedSearchText(value: string | null | undefined): string {
  return (value ?? "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function specificationDimension(name: string): "voltage" | "current" | "power" | "frequency" | null {
  const normalized = name.toLocaleLowerCase("en-GB");
  if (normalized.includes("voltage")) return "voltage";
  if (normalized.includes("current")) return "current";
  if (normalized === "power") return "power";
  if (normalized === "frequency") return "frequency";
  return null;
}

function normalizedSpecificationValue(value: string): string {
  const compact = value.toUpperCase().replace(/\s+/g, "");
  if (/^\d+(?:\.\d+)?$/.test(compact)) return String(Number(compact));
  if (/^\d+(?:\.\d+)?-\d+(?:\.\d+)?$/.test(compact)) return compact.split("-").map(Number).join("-");
  return normalizeMpn(compact);
}

/** Compare normalized search values while treating an unspecified V as AC/DC-compatible. */
export function searchSpecificationsCompatible(
  requested: SearchSpecification,
  candidate: SearchSpecification,
): boolean {
  if (normalizedSpecificationValue(requested.value) !== normalizedSpecificationValue(candidate.value)) return false;
  const requestedUnit = requested.unit?.toUpperCase() ?? "";
  const candidateUnit = candidate.unit?.toUpperCase() ?? "";
  if (requestedUnit === candidateUnit) return true;
  return requestedUnit === "V" && (candidateUnit === "VDC" || candidateUnit === "VAC");
}

function candidateSpecificationValues(specification: SearchSpecification, candidateText: string): string[] {
  if (specification.name === "protocol") {
    return PROTOCOL_PATTERNS.filter(({ pattern }) => pattern.test(candidateText)).map(({ canonical }) => normalizeMpn(canonical));
  }
  if (specification.name === "ingress protection") {
    return [...candidateText.matchAll(/\bIP\s*([0-6][0-9K])\b/gi)].map((match) => normalizeMpn(`IP${match[1]}`));
  }
  const countPatterns: Record<string, RegExp> = {
    channels: /\b(\d{1,4})[\s-]*(?:points?|channels?|ch)\b/gi,
    ports: /\b(\d{1,3})\s*(?:port|ports)\b/gi,
    poles: /\b(\d{1,2})\s*(?:pole|poles)\b/gi,
    phases: /\b([13])\s*[\s-]?(?:phase|ph)\b/gi,
  };
  const countPattern = countPatterns[specification.name];
  if (countPattern) return [...candidateText.matchAll(countPattern)].map((match) => normalizeMpn(match[1]));
  const dimension = specificationDimension(specification.name);
  if (dimension) {
    const coilSpecific = specification.name.startsWith("coil ");
    const ratedCurrentSpecific = specification.name.startsWith("rated current");
    return extractSearchSpecifications(candidateText)
      .filter((candidate) => (
        specificationDimension(candidate.name) === dimension
        && (!coilSpecific || candidate.name.startsWith("coil "))
        && (!ratedCurrentSpecific || candidate.name.startsWith("rated current"))
      ))
      .map((candidate) => `${normalizedSpecificationValue(candidate.value)}${candidate.unit ?? ""}`);
  }
  if (!specification.unit) return [];
  const unit = specification.unit.toUpperCase();
  const escapedUnit = unit.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return [...candidateText.matchAll(new RegExp(`\\b(\\d+(?:\\.\\d+)?)\\s*${escapedUnit}\\b`, "gi"))]
    .map((match) => `${normalizedSpecificationValue(match[1])}${unit}`);
}

function specificationMatches(specification: SearchSpecification, candidateText: string): boolean {
  const values = candidateSpecificationValues(specification, candidateText);
  const expected = `${normalizedSpecificationValue(specification.value)}${specification.unit?.toUpperCase() ?? ""}`;
  if (values.includes(expected)) return true;
  if (specification.unit?.toUpperCase() !== "V") return false;
  const normalizedValue = normalizedSpecificationValue(specification.value);
  return values.some((value) => value === `${normalizedValue}VDC` || value === `${normalizedValue}VAC`);
}

function specificationConflicts(specification: SearchSpecification, candidateText: string): boolean {
  const values = candidateSpecificationValues(specification, candidateText);
  if (values.length === 0) return false;
  return !specificationMatches(specification, candidateText);
}

export function scoreDiscoveryResult(intent: SearchIntent, candidate: DiscoveryCandidate): DiscoveryMatch {
  if (intent.mode === "exact_part") {
    const exact = normalizeMpn(intent.partNumber) === normalizeMpn(candidate.partNumber);
    return { score: exact ? 100 : 0, reasons: exact ? ["Exact part number"] : [] };
  }

  const genericPlan = buildGenericCatalogueDiscoveryPlan(intent.supplierQuery || intent.rawQuery, intent);
  if (genericPlan && !matchesCatalogueIndustrialEvidence({
    title: candidate.description || candidate.partNumber || "",
    mpn: candidate.partNumber,
    manufacturer: candidate.manufacturer,
  }, genericPlan)) return { score: 0, reasons: [] };

  const candidateText = normalizeIndustrialSearchQuery([candidate.partNumber, candidate.manufacturer, candidate.description].filter(Boolean).join(" "));
  const manufacturer = normalizedSearchText(candidate.manufacturer);
  const haystack = normalizedSearchText(candidateText);
  const reasons: string[] = [];
  let score = 0;
  const requestedCategory = getMarketExplorerCategory(intent.category);
  const candidateFamily = detectProductFamily(candidateText);
  const candidateCategory = marketExplorerCategoryForProductFamily(candidateFamily);
  // A switch description may mention its power supply, and a drive may mention
  // a contactor. Those secondary attributes do not make it the requested type.
  const broadFamilies = new Set(["PLC", "I/O module", "Analogue I/O module"]);
  if (intent.productFamily && candidateFamily && intent.productFamily !== candidateFamily
    && !NAMED_PRODUCT_RANGES.has(intent.productFamily) && !NAMED_PRODUCT_RANGES.has(candidateFamily)
    && !broadFamilies.has(intent.productFamily) && !broadFamilies.has(candidateFamily)) {
    return { score: 0, reasons: [] };
  }
  const categoryMatch = Boolean(requestedCategory && candidateCategory === requestedCategory.id);
  if (requestedCategory && candidateCategory && !categoryMatch) return { score: 0, reasons: [] };
  if (requestedCategory && categoryMatch) {
    score += 60;
    reasons.push(`Category: ${requestedCategory.label}`);
  }
  const requestedManufacturer = normalizedSearchText(intent.manufacturer);
  const requestedManufacturerIdentity = canonicalManufacturerIdentity(intent.manufacturer);
  const explicitManufacturerIdentity = canonicalManufacturerIdentity(candidate.manufacturer);
  // Grounded names outside the alias registry can include a company suffix.
  // Compare the explicit label itself, never a compatibility mention in prose.
  const manufacturerLabelMatch = Boolean(requestedManufacturer && manufacturer && (
    manufacturer === requestedManufacturer
    || manufacturer.startsWith(`${requestedManufacturer} `)
    || requestedManufacturer.startsWith(`${manufacturer} `)
  ));
  if (requestedManufacturerIdentity && explicitManufacturerIdentity
    && requestedManufacturerIdentity !== explicitManufacturerIdentity
    && !manufacturerLabelMatch) return { score: 0, reasons: [] };
  const manufacturerMatch = Boolean(requestedManufacturer && (
    (requestedManufacturerIdentity && requestedManufacturerIdentity === explicitManufacturerIdentity)
    || manufacturerLabelMatch
    || haystack.includes(requestedManufacturer)
  ));
  if (requestedManufacturer && !manufacturerMatch) return { score: 0, reasons: [] };
  if (manufacturerMatch) {
    score += 25;
    reasons.push(`Manufacturer: ${intent.manufacturer}`);
  }

  const normalizedFamily = normalizedSearchText(intent.productFamily);
  const familyTokens = normalizedFamily.split(" ").filter((token) => token.length >= 2);
  const familyDefinition = PRODUCT_FAMILIES.find(({ canonical }) => canonical === intent.productFamily);
  // Supplier CPU titles often name the SIMATIC range without the word PLC.
  // Requiring a processor term keeps range accessories and I/O out of that alias.
  const plcProcessorAlias = intent.productFamily === "PLC"
    && /\b(?:cpu|processors?|programmable\s+(?:logic\s+)?controllers?|(?:software|open)\s+controllers?)\b/i.test(candidateText);
  const familyMatch = Boolean(normalizedFamily) && (
    Boolean(familyDefinition?.pattern.test(candidateText))
    || plcProcessorAlias
    || haystack.replace(/\s+/g, "").includes(familyTokens.join(""))
    || familyTokens.every((token) => haystack.includes(token))
  );
  if (familyMatch) {
    score += 25;
    reasons.push(`Product type: ${intent.productFamily}`);
  }

  const keywordTokens = intent.keywords
    .map(normalizedSearchText)
    .filter((token) => token.length >= 2 && !/^\d+$/.test(token));
  const matchedKeywords = keywordTokens.filter((token) => token.split(" ").every((part) => haystack.includes(part)));
  if (keywordTokens.length > 0) {
    score += Math.round(40 * Math.min(1, matchedKeywords.length / keywordTokens.length));
    if (matchedKeywords.length) reasons.push(`Matches: ${matchedKeywords.slice(0, 3).join(", ")}`);
  }

  const matchedSpecifications = intent.specifications.filter((specification) => specificationMatches(specification, candidateText));
  const conflictingSpecification = intent.specifications.some((specification) => specificationConflicts(specification, candidateText));
  if (conflictingSpecification) return { score: 0, reasons: [] };
  if (matchedSpecifications.length) {
    score += Math.min(10, matchedSpecifications.length * 5);
    reasons.push(`Specification: ${matchedSpecifications.map((specification) => `${specification.value}${specification.unit ?? ""}`).join(", ")}`);
  }

  const hasSemanticRequest = Boolean(requestedCategory || normalizedFamily || keywordTokens.length || intent.specifications.length);
  const hasSemanticMatch = categoryMatch || familyMatch || matchedKeywords.length > 0 || matchedSpecifications.length > 0;
  if (normalizedFamily && !familyMatch) return { score: 0, reasons: [] };
  if ((hasSemanticRequest && !hasSemanticMatch) || (!manufacturerMatch && !hasSemanticMatch)) {
    return { score: 0, reasons: [] };
  }
  return { score: Math.min(100, score), reasons };
}

export function searchIntentChips(intent: SearchIntent): string[] {
  return [
    getMarketExplorerCategory(intent.category)?.label,
    intent.manufacturer,
    intent.productFamily,
    intent.quantity ? `Qty ${intent.quantity}` : null,
    intent.maxPriceGbp ? `Under £${intent.maxPriceGbp.toLocaleString("en-GB")}` : null,
    ...intent.conditions.map((condition) => condition.replace(/_/g, " ")),
    ...intent.specifications.map((specification) => `${specification.value}${specification.unit ?? ""}`),
  ].filter((value): value is string => Boolean(value)).slice(0, 8);
}

function specificationChipLabel(specification: SearchSpecification): string {
  const value = `${specification.value}${specification.unit ?? ""}`;
  if (specification.name.startsWith("coil voltage")) return `Coil voltage: ${value}`;
  if (specification.name.startsWith("rated current")) return `Rated current: ${value}`;
  if (specification.name === "channels") return `${value} channels`;
  return value;
}

function specificationQueryFragment(specification: SearchSpecification): string {
  const value = `${specification.value}${specification.unit ?? ""}`;
  if (specification.name.startsWith("coil voltage")) return `${value} coil`;
  if (specification.name.startsWith("rated current")) return `${value} rated current`;
  if (specification.name === "channels") return `${value} channel`;
  return value;
}

export function searchIntentChipModels(
  intent: SearchIntent,
  explicitCategory: MarketExplorerCategoryId | null = null,
): SearchIntentChip[] {
  const category = getMarketExplorerCategory(explicitCategory);
  const identityConstraintCount = [intent.manufacturer, intent.productFamily, ...intent.keywords].filter(Boolean).length
    + intent.specifications.length;
  return [
    category ? { id: "category", kind: "category", label: category.label, removable: identityConstraintCount > 0 } : null,
    intent.manufacturer ? { id: "manufacturer", kind: "manufacturer", label: intent.manufacturer, removable: true } : null,
    intent.productFamily ? { id: "product_family", kind: "product_family", label: intent.productFamily, removable: true } : null,
    intent.quantity ? { id: "quantity", kind: "quantity", label: `Qty ${intent.quantity}`, removable: true } : null,
    intent.maxPriceGbp ? { id: "max_price", kind: "max_price", label: `Under £${intent.maxPriceGbp.toLocaleString("en-GB")}`, removable: true } : null,
    ...intent.conditions.map((condition) => ({
      id: `condition:${condition}`,
      kind: "condition" as const,
      label: condition.replace(/_/g, " "),
      removable: true,
    })),
    ...intent.specifications.map((specification, index) => ({
      id: `specification:${index}`,
      kind: "specification" as const,
      label: specificationChipLabel(specification),
      removable: true,
    })),
    intent.location ? { id: "location", kind: "location", label: intent.location, removable: true } : null,
  ].filter((chip): chip is SearchIntentChip => Boolean(chip)).slice(0, 10);
}

function uniqueQueryFragments(fragments: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  return fragments.filter((fragment): fragment is string => {
    const value = fragment?.trim();
    if (!value) return false;
    const key = normalizedSearchText(value);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function buildSearchQueryWithoutIntentChip(
  intent: SearchIntent,
  chip: SearchIntentChip,
  explicitCategory: MarketExplorerCategoryId | null = null,
): { query: string; category: MarketExplorerCategoryId | null } | null {
  const category = chip.kind === "category" ? null : explicitCategory;
  const categoryDefinition = getMarketExplorerCategory(category);
  const specifications = intent.specifications.filter((_, index) => chip.id !== `specification:${index}`);
  const conditions = intent.conditions.filter((condition) => chip.id !== `condition:${condition}`);
  const identityFragments = uniqueQueryFragments([
    chip.kind === "manufacturer" ? null : intent.manufacturer,
    chip.kind === "product_family" ? null : intent.productFamily,
    ...intent.keywords,
    ...specifications.map(specificationQueryFragment),
  ]);
  const fragments = uniqueQueryFragments([
    identityFragments.length === 0 ? categoryDefinition?.canonicalQuery : null,
    ...identityFragments,
    chip.kind === "quantity" || !intent.quantity ? null : `qty ${intent.quantity}`,
    chip.kind === "max_price" || !intent.maxPriceGbp ? null : `under £${intent.maxPriceGbp}`,
    ...conditions.map((condition) => condition.replace(/_/g, " ")),
    chip.kind === "location" || !intent.location ? null : `delivered to ${intent.location}`,
  ]);
  const query = fragments.join(" ").trim().slice(0, 200);
  return query.length >= 2 ? { query, category } : null;
}
